import { Info } from 'lucide-react'

import { formatWeeklySubscriptionCredits } from '@/lib/credit-balances'
import { Button } from '@/components/ui/button'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import type { TypedCreditBalance } from '@/lib/credits-api'
import { formatCredits } from '@/lib/subscriptions'
import { cn } from '@/lib/utils'

type CreditBalancePopoverProps = {
  balances: TypedCreditBalance[] | undefined
  hasActiveSubscription: boolean
  isLoading?: boolean
  className?: string
}

export function CreditBalancePopover({
  balances,
  hasActiveSubscription,
  isLoading = false,
  className,
}: CreditBalancePopoverProps) {
  return (
    <div className={cn('flex justify-center', className)}>
      <Popover>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-7 gap-1.5 rounded-full bg-primary/10 px-3 py-0 font-nav text-[11px] font-bold uppercase tracking-wider text-primary hover:bg-primary/15 hover:text-primary"
            aria-label="Verfügbare Credits anzeigen"
          >
            Credits
            <Info aria-hidden="true" className="h-3.5 w-3.5" />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          align="center"
          sideOffset={8}
          className="w-[min(19rem,calc(100vw-2rem))] p-3"
        >
          <p className="font-nav text-sm font-semibold text-foreground">Verfügbare Credits</p>

          {isLoading ? (
            <p className="mt-2 text-sm text-muted-foreground">Wird geladen …</p>
          ) : !balances?.length ? (
            <p className="mt-2 text-sm text-muted-foreground">Kein Guthaben vorhanden.</p>
          ) : (
            <div className="mt-2 space-y-3">
              {balances.map((balance) => (
                <div key={balance.credit_type_id}>
                  <p className="text-xs font-semibold uppercase tracking-wide text-foreground">
                    {balance.credit_name}
                  </p>
                  <dl className="mt-1 space-y-1 text-sm">
                    {hasActiveSubscription && (
                      <div className="flex items-start justify-between gap-4">
                        <dt className="text-muted-foreground">Wöchentliche Credits durch Abo</dt>
                        <dd className="shrink-0 font-semibold text-foreground">
                          {formatWeeklySubscriptionCredits(balance)}
                        </dd>
                      </div>
                    )}
                    <div className="flex items-start justify-between gap-4">
                      <dt className="text-muted-foreground">Credits auf dem Guthabenkonto</dt>
                      <dd className="shrink-0 font-semibold text-foreground">
                        {formatCredits(Number(balance.available_balance_credits ?? 0))}
                      </dd>
                    </div>
                  </dl>
                </div>
              ))}
            </div>
          )}
        </PopoverContent>
      </Popover>
    </div>
  )
}
